import React from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import { fade } from '@material-ui/core/styles/colorManipulator';
import clsx from 'clsx';
import moment from 'moment-timezone';
import { formatLicenceStateLabel } from './licenceStateUtils';
import UnitStatusMetaField from './UnitStatusMetaField';

const ALERT_STATUSES = new Set(['blocked', 'suspended']);

const useStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
  },
  // Tight vertical rhythm between label/value rows (~8px)
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minHeight: 24,
  },
  rowLast: {
    marginBottom: 0,
  },
  fieldLabel: {
    flexShrink: 0,
    lineHeight: 1.2,
    color: theme.palette.text.secondary,
  },
  fieldValue: {
    textAlign: 'right',
    lineHeight: 1.3,
    wordBreak: 'break-word',
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  // Sharp full-bleed plate band (edge-to-edge of card); extra gap above/below
  plateBanner: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2),
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderRadius: 0,
    textAlign: 'center',
  },
  plateBannerOk: {
    backgroundColor: fade(theme.palette.primary.main, 0.1),
  },
  plateBannerAlert: {
    backgroundColor: fade(theme.palette.secondary.main, 0.1),
  },
  plateLabel: {
    display: 'block',
    marginBottom: 2,
    lineHeight: 1.2,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontWeight: 600,
  },
  plateValue: {
    lineHeight: 1.3,
    wordBreak: 'break-word',
    fontWeight: 700,
    fontSize: 20,
  },
  plateTextOk: {
    color: theme.palette.primary.main,
  },
  plateTextAlert: {
    color: theme.palette.secondary.main,
  },
}));

/** View-only: blue for pending/active; red for blocked/suspended or past expiration. */
const isPlateAlert = (status, expirationDate) => {
  if (ALERT_STATUSES.has(status)) {
    return true;
  }
  if (expirationDate) {
    const date = moment(expirationDate);
    if (date.isValid() && date.isBefore(moment())) {
      return true;
    }
  }
  // pending / active (and unknown) stay blue unless expired above
  return false;
};

const formatDisplayValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return String(value);
};

const formatDateValue = (value) => {
  if (!value) {
    return '—';
  }
  const date = moment(value);
  return date.isValid() ? date.format('YYYY-MM-DD') : '—';
};

const ViewRow = ({ label, children, classes, last = false }) => (
  <Box className={`${classes.row}${last ? ` ${classes.rowLast}` : ''}`}>
    <Typography variant="caption" className={classes.fieldLabel}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      component="div"
      className={classes.fieldValue}
    >
      {children}
    </Typography>
  </Box>
);

const PermitCardView = ({ values }) => {
  const classes = useStyles();
  const status = String(values?.status || '')
    .toLowerCase()
    .trim();
  const plateAlert = isPlateAlert(status, values?.expiration_date);
  const plateTone = plateAlert ? classes.plateTextAlert : classes.plateTextOk;

  return (
    <Box className={classes.root}>
      <ViewRow label="Status:" classes={classes}>
        {status ? (
          <UnitStatusMetaField status={status} size="small" />
        ) : (
          formatDisplayValue(values?.status)
        )}
      </ViewRow>
      <ViewRow label="Issue Date:" classes={classes}>
        {formatDateValue(values?.issue_date)}
      </ViewRow>
      <ViewRow label="Expiration Date:" classes={classes}>
        {formatDateValue(values?.expiration_date)}
      </ViewRow>
      <Box
        className={clsx(
          classes.plateBanner,
          plateAlert ? classes.plateBannerAlert : classes.plateBannerOk
        )}
      >
        <Typography
          variant="caption"
          className={clsx(classes.plateLabel, plateTone)}
        >
          License Plate
        </Typography>
        <Typography
          component="div"
          className={clsx(classes.plateValue, plateTone)}
        >
          {formatDisplayValue(values?.licence_plate)}
        </Typography>
      </Box>
      <ViewRow label="Licence State:" classes={classes}>
        {formatLicenceStateLabel(values?.licence_state)}
      </ViewRow>
      <ViewRow label="Make:" classes={classes}>
        {formatDisplayValue(values?.make)}
      </ViewRow>
      <ViewRow label="Model:" classes={classes}>
        {formatDisplayValue(values?.model)}
      </ViewRow>
      <ViewRow label="Color:" classes={classes} last>
        {formatDisplayValue(values?.color)}
      </ViewRow>
    </Box>
  );
};

export default PermitCardView;
